import React, { createContext, useCallback, useContext, useMemo, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useFiltersCount } from 'core/FiltersDialog/hooks/useFiltersCount';
import { isArray } from 'lodash';
import { LoggerStatusEnum, sendLog } from 'ui-model';

import { usersResource } from '@lib/resources';

import useNodesCount from 'hooks/useNodesCount';

import { APPOINTMENT_STATUS } from '../FiltersAppointmentStatus/contants';
import { BILLING_STATUS } from '../FiltersBillingStatus/constants';
import useFilters from '../hooks/useFilters';

const FixedFiltersContext = createContext({} as any);
const FiltersTabContext = createContext({} as any);

export const useFiltersContext = () => {
  const ctx = useContext(FixedFiltersContext);

  if (typeof ctx === 'undefined') {
    throw new Error('useFiltersDialogContext must be used within FiltersDialogProvider');
  }

  return ctx;
};

export const useTabContext = () => {
  const ctx = useContext(FiltersTabContext);

  if (typeof ctx === 'undefined') {
    throw new Error('useFiltersDialogContext must be used within FiltersDialogProvider');
  }

  return ctx;
};

export const FixedFiltersProvider: React.FC<any> = ({
  children,
  initialFilters,
  onFiltersChange,
  filterName,
  locked,
  readOnly,
  hideLockButton,
  customValues,
}) => {
  const dispatch = useDispatch();
  const { updater, selectAll, ...filtersTool } = useFilters(initialFilters, onFiltersChange);
  const [selectedTab, setSelectedTab] = useState({ tab: Object.keys(filtersTool.filters)[0], index: 0 });
  const [isFilterLocked, setFilterLocked] = useState<boolean>(locked);

  const tabs = Object.keys(filtersTool.filters);

  const initialCount = Object.entries(filtersTool.filters).reduce((initial, [key, value]) => {
    if (isArray(value)) {
      if (key === 'apptStatus') {
        return {
          ...initial,
          [key]: APPOINTMENT_STATUS.length,
        };
      }
      if (key === 'billingStatus') {
        return {
          ...initial,
          [key]: BILLING_STATUS.length,
        };
      }

      return {
        ...initial,
        [key]: 0,
      };
    }
    return initial;
  }, {});

  const [count, setCount] = useReducer((state: any, newState: any) => ({ ...state, ...newState }), initialCount);

  // This custom count set method is used for custom filters cases like messagesFacility
  // that will pass the filter values to be used directly
  const setCustomCount = React.useCallback((data) => setCount(data), []);

  useNodesCount(
    {
      includeProvider: tabs.includes('provider'),
      includeGroup: tabs.includes('group'),
      includeRenderingProvider: tabs.includes('renderingProvider'),
      includeReferringProvider: tabs.includes('referringProvider'),
      includeApptType: tabs.includes('apptType'),
      includeFacility: tabs.includes('facility'),
      includeForms: tabs.includes('forms'),
      includeSchedulingPlus: tabs.includes('schedulingPlus'),
      includeInsurance: tabs.includes('insurance'),
    },
    (data) => setCount(data),
    !customValues,
  );

  const restCount = useFiltersCount({ includeUser: tabs.includes('user') });

  const toggleFilterLocked = useCallback(() => {
    setFilterLocked((isLocked) => !isLocked);
    // useCallback makes the data get an old value of isFilterLocked
    const data = { [filterName]: { ...filtersTool.filters, locked: !isFilterLocked } };
    dispatch(
      usersResource.request({
        url: `/users/filters`,
        method: 'post',
        data,
        meta: {
          callbackError: (err) => {
            sendLog(`Error while saving filters on ${filterName}`, { errorContext: err }, LoggerStatusEnum.error);
          },
        },
      }),
    );
  }, [filterName, filtersTool.filters, isFilterLocked, dispatch]);

  const unlockFilter = useCallback(() => {
    if (isFilterLocked) {
      setFilterLocked(false);
      const data = { [filterName]: { locked: !isFilterLocked } };
      dispatch(
        usersResource.request({
          url: `/users/filters`,
          method: 'post',
          data,
          meta: {
            callbackError: (err) => {
              sendLog(`Error while saving filters on ${filterName}`, { errorContext: err }, LoggerStatusEnum.error);
            },
          },
        }),
      );
    }
  }, [dispatch, filterName, isFilterLocked]);

  const selectAllWrapped = useCallback(
    (filterType: string) => {
      unlockFilter();
      return selectAll(filterType);
    },
    [selectAll, unlockFilter],
  );

  const updaterWrapped = useCallback(
    (key: string) => (item: string) => {
      unlockFilter();

      return updater(key)(item, count[key]);
    },
    [unlockFilter, updater, count],
  );

  const tabContextValue = {
    tabs,
    selectedTab,
    changeTab: setSelectedTab,
    isFilterLocked,
    toggleFilterLocked,
  };

  const filterContextValue = useMemo(
    () => ({
      ...filtersTool,
      updater: updaterWrapped,
      selectAll: selectAllWrapped,
      count: { ...count, ...restCount },
      readOnly,
      hideLockButton,
      setCount,
      setCustomCount,
    }),
    [filtersTool, updaterWrapped, selectAllWrapped, count, restCount, readOnly, hideLockButton, setCustomCount],
  );

  return (
    <FixedFiltersContext.Provider value={filterContextValue}>
      <FiltersTabContext.Provider value={tabContextValue}>{children}</FiltersTabContext.Provider>
    </FixedFiltersContext.Provider>
  );
};
// FIXME match filename with default export
export default FixedFiltersProvider;
